import { apiPost } from './api';
import { trackActivityEvent } from './activityTracker';

export type PlanFlowPhase =
	| 'questionnaire_handoff'
	| 'generate_plan_bootstrap'
	| 'generate_plan_retry'
	| 'ai_generation_request'
	| 'ai_generation_response';

export type PlanFlowStorageSnapshot = {
	hasPendingQuestionnaire: boolean;
	pendingQuestionnaireBytes: number;
	hasGeneratePlanReview: boolean;
	generatePlanReviewBytes: number;
	hasGardenQuestionnaireDraft: boolean;
	hasCurrentDraftPlanId: boolean;
	hasNavigationStateHandoff: boolean;
	photoBeforeIsDataUrl: boolean;
	photoBeforeUrlLength: number;
};

function safeStorageLength(key: string, store: Storage): number {
	try {
		const raw = store.getItem(key);
		return raw ? raw.length : 0;
	} catch {
		return -1;
	}
}

function safeStorageHas(key: string, store: Storage): boolean {
	try {
		return store.getItem(key) != null;
	} catch {
		return false;
	}
}

/** Non-PII snapshot of browser storage relevant to plan creation. */
export function getPlanFlowStorageSnapshot(options?: {
	hasNavigationStateHandoff?: boolean;
	photoBeforeUrl?: string | null;
}): PlanFlowStorageSnapshot {
	const photo = options?.photoBeforeUrl ?? null;
	return {
		hasPendingQuestionnaire: safeStorageHas('pendingQuestionnaire', localStorage),
		pendingQuestionnaireBytes: safeStorageLength('pendingQuestionnaire', localStorage),
		hasGeneratePlanReview: safeStorageHas('generatePlanReview', sessionStorage),
		generatePlanReviewBytes: safeStorageLength('generatePlanReview', sessionStorage),
		hasGardenQuestionnaireDraft: safeStorageHas('gardenQuestionnaireDraft', localStorage),
		hasCurrentDraftPlanId: safeStorageHas('currentDraftPlanId', localStorage),
		hasNavigationStateHandoff: !!options?.hasNavigationStateHandoff,
		photoBeforeIsDataUrl: typeof photo === 'string' && photo.startsWith('data:'),
		photoBeforeUrlLength: typeof photo === 'string' ? photo.length : 0
	};
}

function storageErrorName(err: unknown): string | undefined {
	if (err instanceof DOMException) return err.name;
	if (err && typeof err === 'object' && 'name' in err) return String((err as { name: unknown }).name);
	return undefined;
}

/**
 * Log plan-creation diagnostics to activity_events and server plan-flow-debug.log.
 * Never blocks UX.
 */
export function logPlanFlowEvent(
	phase: PlanFlowPhase,
	event: string,
	details: Record<string, unknown> = {}
): void {
	if (typeof window === 'undefined') return;

	const payload = {
		phase,
		event,
		page_path: window.location.pathname,
		...details
	};

	console.warn(`[plan-flow] ${phase}/${event}`, payload);

	void trackActivityEvent({
		event_name: `plan_flow_${event}`,
		page_path: window.location.pathname,
		properties: payload
	});

	void apiPost('/plan-flow-log', {
		phase,
		event,
		page_path: window.location.pathname,
		details: payload
	}).catch(() => {
		/* ignore */
	});
}

export function logQuestionnaireHandoff(details: {
	localStorageSaved: boolean;
	storageError?: unknown;
	usedNavigationState: boolean;
	questionnaireSummary?: Record<string, unknown>;
}): void {
	const storageError = details.storageError
		? {
				name: storageErrorName(details.storageError),
				message:
					details.storageError instanceof Error
						? details.storageError.message.slice(0, 200)
						: String(details.storageError).slice(0, 200)
			}
		: undefined;

	logPlanFlowEvent('questionnaire_handoff', details.localStorageSaved ? 'handoff_ok' : 'handoff_storage_failed', {
		local_storage_saved: details.localStorageSaved,
		used_navigation_state: details.usedNavigationState,
		storage_error: storageError,
		...getPlanFlowStorageSnapshot(),
		...(details.questionnaireSummary ?? {})
	});
}

export type PendingQuestionnairePayload = {
	questionnaire: Record<string, unknown>;
	contactEmail?: string;
};

/** Resolve questionnaire handoff: review session → router state → localStorage. */
export function resolvePendingQuestionnaireHandoff(locationState: unknown): {
	source: 'review_session' | 'navigation_state' | 'local_storage';
	data: PendingQuestionnairePayload;
} | null {
	const restoreReview = (() => {
		try {
			const raw = sessionStorage.getItem('generatePlanReview');
			if (!raw) return null;
			const parsed = JSON.parse(raw) as { questionnaire?: unknown; aiContent?: unknown };
			if (parsed?.questionnaire && parsed?.aiContent) {
				return { questionnaire: parsed.questionnaire, contactEmail: '' };
			}
		} catch {
			/* ignore */
		}
		return null;
	})();
	if (restoreReview?.questionnaire && typeof restoreReview.questionnaire === 'object') {
		return {
			source: 'review_session',
			data: {
				questionnaire: restoreReview.questionnaire as Record<string, unknown>,
				contactEmail: ''
			}
		};
	}

	const state = locationState as { pendingQuestionnaire?: PendingQuestionnairePayload } | null;
	if (state?.pendingQuestionnaire?.questionnaire) {
		return { source: 'navigation_state', data: state.pendingQuestionnaire };
	}

	try {
		const raw = localStorage.getItem('pendingQuestionnaire');
		if (!raw) return null;
		const parsed = JSON.parse(raw) as PendingQuestionnairePayload;
		if (!parsed?.questionnaire) return null;
		return { source: 'local_storage', data: parsed };
	} catch {
		return null;
	}
}

export function logGeneratePlanBootstrap(details: {
	source: 'review_session' | 'navigation_state' | 'local_storage' | 'none';
	reason?: string;
	errorCode?: string;
}): void {
	const event =
		details.source === 'none'
			? details.errorCode === 'invalid_json'
				? 'missing_invalid_questionnaire'
				: 'missing_no_questionnaire'
			: 'bootstrap_ok';

	logPlanFlowEvent('generate_plan_bootstrap', event, {
		source: details.source,
		reason: details.reason,
		...getPlanFlowStorageSnapshot()
	});
}
